import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Search,
  Filter,
  RefreshCw,
  FileText,
  Shield,
  Users,
  Award,
  Mail,
  Settings,
  Building2,
  GraduationCap,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash2,
  Eye,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  User,
  LogIn,
  LogOut,
  Key
} from 'lucide-react';

const AuditLogs = () => {
  usePageTitle('Audit Logs');
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [timeframeFilter, setTimeframeFilter] = useState('');
  
  // Detail modal
  const [selectedLog, setSelectedLog] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  
  // Quick timeframe presets
  const applyTimeframe = (timeframe) => {
    const today = new Date();
    const formatDate = (d) => d.toISOString().split('T')[0];
    
    let from, to;
    
    switch (timeframe) {
      case 'today':
        from = to = formatDate(today);
        break;
      case 'yesterday':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        from = to = formatDate(yesterday);
        break;
      case 'last7days':
        const week = new Date(today);
        week.setDate(week.getDate() - 7);
        from = formatDate(week);
        to = formatDate(today);
        break;
      case 'thisMonth':
        from = formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
        to = formatDate(today);
        break;
      case 'lastMonth':
        const lastMonthStart = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);
        from = formatDate(lastMonthStart);
        to = formatDate(lastMonthEnd);
        break;
      case 'thisYear':
        from = formatDate(new Date(today.getFullYear(), 0, 1));
        to = formatDate(today);
        break;
      case 'all':
      default:
        from = '';
        to = '';
        break;
    }
    
    setDateFrom(from);
    setDateTo(to);
    setTimeframeFilter(timeframe);
    setPage(1);
  };
  
  // Available filters
  const [availableActions, setAvailableActions] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('page_size', 25);
      
      if (searchQuery) params.append('search', searchQuery);
      if (categoryFilter) params.append('category', categoryFilter);
      if (actionFilter) params.append('action', actionFilter);
      if (statusFilter) params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const response = await axios.get(`${API_URL}/audit-logs?${params.toString()}`);
      setLogs(response.data.items);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, categoryFilter, actionFilter, statusFilter, dateFrom, dateTo]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/audit-logs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchFilters = async () => {
    try {
      const response = await axios.get(`${API_URL}/audit-logs/actions`);
      setAvailableActions(response.data.actions);
      setAvailableCategories(response.data.categories);
    } catch (error) {
      console.error('Failed to fetch filters:', error);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    fetchStats();
    fetchFilters();
  }, []);

  const handleExport = async () => {
    if (!dateFrom || !dateTo) {
      toast.error('Please select a date range for export');
      return;
    }
    
    try {
      const params = new URLSearchParams();
      params.append('date_from', dateFrom);
      params.append('date_to', dateTo);
      if (categoryFilter) params.append('category', categoryFilter);
      
      const response = await axios.get(`${API_URL}/audit-logs/export?${params.toString()}`);
      
      // Download as JSON
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${dateFrom}_${dateTo}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${response.data.total_records} records`);
    } catch (error) {
      toast.error('Failed to export logs');
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'authentication': <LogIn className="w-4 h-4" />,
      'user_management': <Users className="w-4 h-4" />,
      'certificates': <Award className="w-4 h-4" />,
      'classes': <GraduationCap className="w-4 h-4" />,
      'students': <User className="w-4 h-4" />,
      'companies': <Building2 className="w-4 h-4" />,
      'email_marketing': <Mail className="w-4 h-4" />,
      'settings': <Settings className="w-4 h-4" />,
      'training_centers': <Building2 className="w-4 h-4" />,
      'instructors': <User className="w-4 h-4" />
    };
    return icons[category] || <FileText className="w-4 h-4" />;
  };

  const getCategoryColor = (category) => {
    const colors = {
      'authentication': 'bg-purple-100 text-purple-700',
      'user_management': 'bg-blue-100 text-blue-700',
      'certificates': 'bg-green-100 text-green-700',
      'classes': 'bg-yellow-100 text-yellow-700',
      'students': 'bg-indigo-100 text-indigo-700',
      'companies': 'bg-orange-100 text-orange-700',
      'email_marketing': 'bg-pink-100 text-pink-700',
      'settings': 'bg-gray-100 text-gray-700',
      'training_centers': 'bg-teal-100 text-teal-700',
      'instructors': 'bg-cyan-100 text-cyan-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  const getActionLabel = (action) => {
    return action.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const resetFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setActionFilter('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
    setTimeframeFilter('');
    setPage(1);
  };

  // Redirect non-admins
  if (user?.role !== 'admin') {
    return (
      <div className="p-8 text-center">
        <Shield className="w-16 h-16 mx-auto text-slate-300 mb-4" />
        <h2 className="text-xl font-semibold text-slate-700">Access Denied</h2>
        <p className="text-slate-500 mt-2">Only administrators can view audit logs.</p>
        <Button onClick={() => navigate('/dashboard')} className="mt-4">
          Return to Dashboard
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin-settings')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Audit Logs
            </h1>
            <p className="text-slate-500 text-sm">Track system activities and user actions</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleExport} disabled={!dateFrom || !dateTo}>
            <Download className="w-4 h-4 mr-2" />
            Export
          </Button>
          <Button variant="outline" onClick={() => { fetchLogs(); fetchStats(); }}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Logs</p>
                  <p className="text-2xl font-bold text-slate-900">{stats.total_logs.toLocaleString()}</p>
                </div>
                <FileText className="w-8 h-8 text-slate-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Today</p>
                  <p className="text-2xl font-bold text-[#2C9DE1]">{stats.logs_today.toLocaleString()}</p>
                </div>
                <Clock className="w-8 h-8 text-[#2C9DE1]/30" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">This Week</p>
                  <p className="text-2xl font-bold text-green-600">{stats.logs_this_week.toLocaleString()}</p>
                </div>
                <CheckCircle className="w-8 h-8 text-green-300" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Categories</p>
                  <p className="text-2xl font-bold text-purple-600">{Object.keys(stats.by_category).length}</p>
                </div>
                <Shield className="w-8 h-8 text-purple-300" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          {/* Quick Timeframe Buttons */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="text-sm text-slate-500 mr-2 self-center">Quick filters:</span>
            {[
              { key: 'today', label: 'Today' },
              { key: 'yesterday', label: 'Yesterday' },
              { key: 'last7days', label: 'Last 7 Days' },
              { key: 'thisMonth', label: 'This Month' },
              { key: 'lastMonth', label: 'Last Month' },
              { key: 'thisYear', label: 'This Year' },
              { key: 'all', label: 'All Time' }
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant={timeframeFilter === key ? "default" : "outline"}
                size="sm"
                onClick={() => applyTimeframe(key)}
                className={timeframeFilter === key ? "bg-[#2C9DE1] hover:bg-[#1A7BB8]" : ""}
              >
                {label}
              </Button>
            ))}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2">
              <Label className="text-xs text-slate-500 mb-1">Search</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search by email, target..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="audit-search-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1">Category</Label>
              <Select value={categoryFilter || "all"} onValueChange={(v) => setCategoryFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="audit-category-filter">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {availableCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1">Status</Label>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="audit-status-filter">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failure">Failure</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1">From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setTimeframeFilter(''); }}
                data-testid="audit-date-from"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1">To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setTimeframeFilter(''); }}
                data-testid="audit-date-to"
              />
            </div>
          </div>
          <div className="flex justify-end mt-4">
            <Button variant="ghost" size="sm" onClick={resetFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Activity Log</CardTitle>
            <p className="text-sm text-slate-500">{total.toLocaleString()} total records</p>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-[#2C9DE1]" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <p className="text-slate-500">No audit logs found</p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="w-[180px]">Timestamp</TableHead>
                    <TableHead className="w-[120px]">Category</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Target</TableHead>
                    <TableHead className="w-[80px]">Status</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.id} className="hover:bg-slate-50/50" data-testid={`audit-row-${log.id}`}>
                      <TableCell className="text-xs text-slate-500">
                        {formatTimestamp(log.timestamp)}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getCategoryColor(log.category)} text-xs`}>
                          <span className="mr-1">{getCategoryIcon(log.category)}</span>
                          {log.category?.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">
                        {getActionLabel(log.action)}
                      </TableCell>
                      <TableCell className="text-sm">
                        <div>{log.user_email || '-'}</div>
                        {log.user_role && (
                          <span className="text-xs text-slate-400">{log.user_role}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {log.target_name || log.target_id || '-'}
                        {log.target_type && (
                          <span className="text-xs text-slate-400 ml-1">({log.target_type})</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {log.status === 'success' ? (
                          <Badge className="bg-green-100 text-green-700 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            OK
                          </Badge>
                        ) : (
                          <Badge className="bg-red-100 text-red-700 text-xs">
                            <XCircle className="w-3 h-3 mr-1" />
                            Fail
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedLog(log);
                            setDetailOpen(true);
                          }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages}
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Log Details</DialogTitle>
            <DialogDescription>
              Full details of the audit log entry
            </DialogDescription>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">Timestamp</Label>
                  <p className="font-medium">{formatTimestamp(selectedLog.timestamp)}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Status</Label>
                  <p>
                    {selectedLog.status === 'success' ? (
                      <Badge className="bg-green-100 text-green-700">Success</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700">Failure</Badge>
                    )}
                  </p>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Action</Label>
                <p className="font-medium">{getActionLabel(selectedLog.action)}</p>
                <Badge className={`${getCategoryColor(selectedLog.category)} mt-1`}>
                  {selectedLog.category?.replace('_', ' ')}
                </Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-500">User</Label>
                  <p className="font-medium">{selectedLog.user_email || '-'}</p>
                  <p className="text-xs text-slate-400">{selectedLog.user_role}</p>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">IP Address</Label>
                  <p className="font-medium">{selectedLog.ip_address || '-'}</p>
                </div>
              </div>
              
              <div>
                <Label className="text-xs text-slate-500">Target</Label>
                <p className="font-medium">{selectedLog.target_name || selectedLog.target_id || '-'}</p>
                {selectedLog.target_type && (
                  <p className="text-xs text-slate-400">Type: {selectedLog.target_type}</p>
                )}
              </div>
              
              {selectedLog.details && Object.keys(selectedLog.details).length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Additional Details</Label>
                  <pre className="mt-1 p-3 bg-slate-50 rounded-lg text-xs overflow-auto max-h-40">
                    {JSON.stringify(selectedLog.details, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AuditLogs;
